import { useCallback, useState } from 'react';
import Dialpad from 'flow/components/Dialpad';
import ContactList from 'flow/components/nav/contact-list/ContactList';
import { ContactListContact } from 'flow/selectors/contact-list-selectors';
import PbxList from 'flow/components/nav/PbxList_TW';
import { PbxListDepartment } from 'flow/components/nav/pbx-list-rows';
import SearchBar from 'flow/components/SearchBar';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Tabs from 'flow/components/ui-components/Tabs';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { Channel } from 'flow/types/Channel';
import { Extension } from 'flow/types/Extension';
import {
    Conference,
    Connection,
    QueueSortingData,
    Refer,
} from 'flow/types/Pbx';
import Queue from 'flow/types/Queue';
import { TypedMap } from 'flow/types/TypedMap';
import { Voicemail } from 'flow/types/Voicemail';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { stripAndConvertNumberToE164 } from 'flow/utils/call-utils';
import { getFixedOrTechnicalOrMobileNumberFromContact } from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';

type TabKey = 'contacts' | 'pbx' | 'dialpad';

// The PBX list memoizes its rows on this, so it must not be a new array on
// every render.
const PBX_DEPARTMENTS: PbxListDepartment[] = ['favorites', 'ivr', 'queues'];

interface Props {
    title?: string;
    contactSelectedAction?: (
        extension: TypedMap<Extension>,
        number: string,
    ) => void;
    numberSelectedAction?: (number: string) => void;
    pbxSelectedAction?: (
        number: string,
        selectedPbxTransferNumber: string,
    ) => void;
}

const NewCallModal = ({
    title,
    contactSelectedAction,
    numberSelectedAction,
    pbxSelectedAction,
}: Props) => {
    const { i18next } = useAppContext();
    const ownCountryCode = useAppSelector(selectUserCountryCode);

    const [selectedTab, setSelectedTab] = useState<TabKey>('contacts');
    const [filterText, setFilterText] = useState('');

    const [selectedExtension, setSelectedExtension] = useState(null);
    const [selectedPbx, setSelectedPbx] = useState(null);
    const [selectedDialup, setSelectedDialup] = useState(null);

    const { isSinglePaneMode } = useSinglePaneMode();

    const tabs = [
        { key: 'contacts', label: i18next.t('Call.Transfer.Contacts') },
        { key: 'pbx', label: i18next.t('Call.Transfer.Pbx') },
        {
            key: 'dialpad',
            label: i18next.t('central_switch.dialpad.description'),
        },
    ];

    const getSelectedExtensionTransferNumber = () => {
        const contact = selectedExtension?.contact;

        if (contact) {
            const thirdPartyContactNumber = contact.getIn(
                ['phoneNumber', 'number'],
                null,
            );

            return (
                getFixedOrTechnicalOrMobileNumberFromContact(contact) ||
                thirdPartyContactNumber
            );
        }

        return stripAndConvertNumberToE164(selectedExtension, ownCountryCode);
    };

    const getSelectedPbxTransferNumber = () => {
        if (selectedPbx.item) {
            return selectedPbx.item.getIn(['number', 'number']);
        }
        return stripAndConvertNumberToE164(selectedPbx, ownCountryCode);
    };

    const selectPbx = useCallback(
        (
            key: string,
            item: TypedMap<
                | Channel
                | Conference
                | Connection
                | QueueSortingData
                | Refer
                | Voicemail
            >,
        ) => {
            setSelectedPbx({ key, item });
        },
        [],
    );

    const selectExtension = useCallback(
        (key: string, contact: ContactListContact, status: string) => {
            if (contact) {
                setSelectedExtension({ key, contact, status });
            }
        },
        [],
    );

    const onAccept = () => {
        if (selectedTab === 'contacts') {
            contactSelectedAction(
                selectedExtension,
                getSelectedExtensionTransferNumber(),
            );
        } else if (selectedTab === 'pbx') {
            pbxSelectedAction(selectedPbx, getSelectedPbxTransferNumber());
        } else {
            numberSelectedAction(
                stripAndConvertNumberToE164(selectedDialup, ownCountryCode),
            );
        }
    };

    const disableAcceptButton = () => {
        if (selectedTab === 'contacts') {
            return selectedExtension === null;
        }

        if (selectedTab === 'pbx') {
            return selectedPbx === null;
        }

        return isEmptyNullOrUndefined(selectedDialup);
    };

    const onClickTabs = (tab: TabKey) => {
        if (selectedTab !== tab) {
            setSelectedDialup(null);
            setSelectedExtension(null);
            setSelectedPbx(null);

            setSelectedTab(tab);
        }
    };

    return (
        <BaseModal
            open={true}
            header={title}
            disableSubmit={disableAcceptButton()}
            onSubmit={onAccept}
            confirmButtonText={i18next.t('accept')}
        >
            <div className="transfer-modal flex h-full flex-col gap-3">
                <Tabs
                    tabs={tabs}
                    onClick={({ key }) => onClickTabs(key as TabKey)}
                />

                {selectedTab !== 'dialpad' ? (
                    <>
                        <SearchBar
                            onChange={setFilterText}
                            initialSearchText={filterText}
                            enableMakingCalls={false}
                            searchInputClassName="dark:!bg-gray-300"
                        />

                        <div
                            className={twMerge(
                                'w-full',
                                isSinglePaneMode ? 'h-full' : 'h-[300px]',
                            )}
                        >
                            {selectedTab === 'contacts' && (
                                <ContactList
                                    selected={selectedExtension?.key}
                                    onItemSelected={selectExtension}
                                    extensionFilter={filterText}
                                    showActionButtons={false}
                                    disableItemsWithoutNumber={true}
                                    showOverviewRow={false}
                                    selectionFollowsFocus={false}
                                    refocusOnReturn={false}
                                />
                            )}

                            {selectedTab === 'pbx' && (
                                <PbxList
                                    onItemSelected={selectPbx}
                                    filterText={filterText}
                                    // null, not undefined: with undefined, the
                                    // list picks its default item.
                                    selectedPbx={selectedPbx?.key ?? null}
                                    selectDefaultItem={true}
                                    enableCallButton={false}
                                    marksOpenPage={false}
                                    enabledDepartments={PBX_DEPARTMENTS}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <Dialpad
                        setContact={setSelectedDialup}
                        disableCallButton={true}
                        onEnter={onAccept}
                    />
                )}
            </div>
        </BaseModal>
    );
};

export default NewCallModal;
